import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { Users, User as UserIcon, X, Lock, Info, Loader2, ChevronRight, ChevronDown } from 'lucide-react'
import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import FolderShareDialogSkeleton from './FolderShareDialogSkeleton'
import { DOC_LEVELS as LEVELS, levelAtLeast } from '../utils/permission-levels'

interface Props {
  folderId: string | null
  projectId: string
  onClose: () => void
}

interface Entry {
  userId?: string | null
  roleId?: string | null
  level: string
  inherited: boolean
  user?: { id: string; name: string; email: string } | null
  projectRole?: { id: string; name: string } | null
  fromFolder?: { id: string; name: string } | null
}

const DEFAULT_LEVEL_LABELS: Record<string, string> = {
  VIEWER: 'Viewer',
  COMMENTER: 'Commenter',
  EDITOR: 'Editor',
  MANAGER: 'Manager',
  ADMIN: 'Admin',
}

interface MemberAccessEntry {
  userId: string
  level: string | null
  source: string | null
  user: { id: string; name: string; email: string }
}

const LEVEL_ORDER: Record<string, number> = { VIEWER: 1, COMMENTER: 2, EDITOR: 3, MANAGER: 4, ADMIN: 5 }

const FolderShareDialog: React.FC<Props> = ({ folderId, projectId, onClose }) => {
  const { t } = useTranslation()
  const [subject, setSubject] = useState('')        // "u:<id>" | "r:<id>"
  const [newLevel, setNewLevel] = useState('VIEWER')
  const [pendingKey, setPendingKey] = useState<string | null>(null)
  const [showMembers, setShowMembers] = useState(false)

  // Dialog instance is rendered unconditionally by the caller — reset editor
  // state whenever the target folder changes so a stale subject/level picked
  // for one folder can't be silently granted on another.
  useEffect(() => {
    setSubject('')
    setNewLevel('VIEWER')
    setPendingKey(null)
    setShowMembers(false)
  }, [folderId])

  const levelLabel = (level: string) => t(`DocControl_role_${level.toLowerCase()}`, DEFAULT_LEVEL_LABELS[level] ?? level)

  const { data, loading, refetch } = useQuery(Query.DOC_FOLDER_SHARING, {
    variables: { folderId },
    skip: !folderId,
    fetchPolicy: 'network-only',
  })
  const { data: projectData } = useQuery(Query.PROJECT, {
    variables: { id: projectId },
    skip: !projectId,
  })
  const { data: rolesData } = useQuery(Query.PROJECT_ROLES)

  const [grant, { loading: granting }] = useMutation(Mutation.GRANT_DOC_FOLDER_PERMISSION, {
    onError: (e) => toastr.error(t('Common_error', 'Error'), e.message),
  })
  const [revoke] = useMutation(Mutation.REVOKE_DOC_FOLDER_PERMISSION, {
    onError: (e) => toastr.error(t('Common_error', 'Error'), e.message),
  })
  const [setRestricted, { loading: settingRestricted }] = useMutation(Mutation.SET_DOC_FOLDER_RESTRICTED, {
    onError: (e) => toastr.error(t('Common_error', 'Error'), e.message),
  })

  const folder = data?.docFolder
  const entries: Entry[] = data?.docFolderEffectivePermissions ?? []
  const myLevel: string | null = folder?.effectivePermission?.level ?? null
  const canManage = levelAtLeast(myLevel, 'MANAGER')

  // Per-member effective access — loaded lazily when the section is expanded
  const { data: membersData, loading: membersLoading } = useQuery(Query.DOC_FOLDER_MEMBER_ACCESS, {
    variables: { folderId },
    skip: !folderId || !showMembers,
    fetchPolicy: 'network-only',
  })
  const memberEntries: MemberAccessEntry[] = useMemo(() => {
    const list: MemberAccessEntry[] = membersData?.docFolderMemberAccess ?? []
    return [...list].sort((a, b) => {
      const la = a.level ? LEVEL_ORDER[a.level] ?? 0 : -1
      const lb = b.level ? LEVEL_ORDER[b.level] ?? 0 : -1
      if (la !== lb) return lb - la
      return (a.user?.name ?? '').localeCompare(b.user?.name ?? '')
    })
  }, [membersData])

  const sourceLabel = (source: string | null) => {
    switch (source) {
      case 'admin':     return t('DocControl_access_source_admin', 'module admin')
      case 'grant':     return t('DocControl_access_source_grant', 'direct')
      case 'inherited': return t('DocControl_access_source_inherited', 'inherited')
      case 'implicit':  return t('DocControl_access_source_implicit', 'project member')
      default:          return null
    }
  }

  const takenKeys = useMemo(
    () => new Set(entries.filter((e) => !e.inherited).map((e) => (e.userId ? `u:${e.userId}` : `r:${e.roleId}`))),
    [entries],
  )
  const projectUsers = (projectData?.project?.users ?? []).filter(
    (pu: any) => !takenKeys.has(`u:${pu.user.id}`),
  )
  const roles = (rolesData?.projectRoles ?? []).filter((r: any) => !takenKeys.has(`r:${r.id}`))

  const subjectVars = (key: string) =>
    key.startsWith('u:') ? { userId: key.slice(2) } : { roleId: key.slice(2) }

  const handleAdd = async () => {
    if (!subject || !folderId) return
    try {
      await grant({ variables: { folderId, level: newLevel, ...subjectVars(subject) } })
      await refetch()
    } finally {
      setSubject('')
      setNewLevel('VIEWER')
    }
  }

  const entryKey = (e: Entry) => (e.userId ? `u:${e.userId}` : `r:${e.roleId}`)

  const handleLevelChange = async (e: Entry, level: string) => {
    if (!folderId) return
    setPendingKey(entryKey(e))
    // changing an inherited entry creates a direct override on this folder
    try {
      await grant({ variables: { folderId, level, ...subjectVars(entryKey(e)) } })
      await refetch()
    } finally {
      setPendingKey(null)
    }
  }

  const handleRemove = async (e: Entry) => {
    if (!folderId || e.inherited) return
    setPendingKey(entryKey(e))
    try {
      await revoke({ variables: { folderId, ...subjectVars(entryKey(e)) } })
      await refetch()
    } finally {
      setPendingKey(null)
    }
  }

  const handleRestricted = async (value: boolean) => {
    if (!folderId) return
    await setRestricted({ variables: { folderId, restricted: value } })
    await refetch()
  }

  return (
    <Dialog open={!!folderId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            {t('DocControl_manage_access', 'Manage access')}{folder && <> — {folder.name}</>}
          </DialogTitle>
        </DialogHeader>

        {loading && !data ? (
          <FolderShareDialogSkeleton />
        ) : !folder ? (
          <p className="text-sm text-muted-foreground">
            {t('DocControl_no_access_folder', "You don't have access to this folder")}
          </p>
        ) : (
          <div className="space-y-4">
            {canManage && (
              <div className="flex items-center gap-2">
                <Select value={subject} onValueChange={setSubject}>
                  <SelectTrigger className="h-9 flex-1 text-sm">
                    <SelectValue placeholder={t('DocControl_share_add_placeholder', 'Add person or role...')} />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((r: any) => (
                      <SelectItem key={`r:${r.id}`} value={`r:${r.id}`}>
                        <span className="flex items-center gap-2">
                          <Users className="h-3.5 w-3.5" />{r.name}
                        </span>
                      </SelectItem>
                    ))}
                    {projectUsers.map((pu: any) => (
                      <SelectItem key={`u:${pu.user.id}`} value={`u:${pu.user.id}`}>
                        <span className="flex items-center gap-2">
                          <UserIcon className="h-3.5 w-3.5" />{pu.user.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={newLevel} onValueChange={setNewLevel}>
                  <SelectTrigger className="h-9 w-[150px] text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LEVELS.map((l) => (
                      <SelectItem key={l} value={l}>{levelLabel(l)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" className="h-9" onClick={handleAdd} disabled={!subject || granting}>
                  {granting ? <Loader2 className="h-4 w-4 animate-spin" /> : t('DocControl_add', 'Add')}
                </Button>
              </div>
            )}

            <div className="space-y-1">
              {entries.map((e) => {
                const key = entryKey(e)
                return (
                  <div key={key} className="flex items-center gap-2 py-1">
                    {e.userId ? (
                      <UserIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                    ) : (
                      <Users className="h-4 w-4 text-muted-foreground shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <span className="text-sm truncate block">
                        {e.user?.name ?? e.projectRole?.name}
                      </span>
                      {e.inherited && e.fromFolder && (
                        <span className="text-xs text-muted-foreground">
                          {t('DocControl_access_inherited_from', 'Inherited from {{name}}', { name: e.fromFolder.name })}
                        </span>
                      )}
                    </div>
                    {pendingKey === key ? (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    ) : canManage ? (
                      <>
                        <Select value={e.level} onValueChange={(v) => handleLevelChange(e, v)}>
                          <SelectTrigger className="h-8 w-[140px] text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {LEVELS.map((l) => (
                              <SelectItem key={l} value={l}>{levelLabel(l)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {!e.inherited && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            aria-label={t('DocControl_revoke', 'Remove access')}
                            onClick={() => handleRemove(e)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </>
                    ) : (
                      <span className="text-sm text-muted-foreground">
                        {levelLabel(e.level)}
                      </span>
                    )}
                  </div>
                )
              })}
              {entries.length === 0 && (
                <p className="text-sm text-muted-foreground">{t('DocControl_no_permissions', 'No permissions set')}</p>
              )}
            </div>

            <div className="pt-3 border-t">
              <button
                type="button"
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
                onClick={() => setShowMembers((v) => !v)}
              >
                {showMembers ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                {t('DocControl_member_access_toggle', "All members' access")}
              </button>
              {showMembers && (
                <div className="mt-2 space-y-1 max-h-56 overflow-y-auto">
                  {membersLoading ? (
                    <div className="flex justify-center py-3">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    memberEntries.map((m) => (
                      <div key={m.userId} className="flex items-center gap-2 py-1">
                        <UserIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm truncate block">{m.user?.name || m.user?.email}</span>
                          <span className="text-xs text-muted-foreground truncate block">{m.user?.email}</span>
                        </div>
                        {m.level ? (
                          <span className="text-sm whitespace-nowrap">
                            {levelLabel(m.level)}
                            <span className="text-xs text-muted-foreground"> · {sourceLabel(m.source)}</span>
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground whitespace-nowrap">
                            {t('DocControl_access_none', 'No access')}
                          </span>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 pt-3 border-t">
              {canManage ? (
                <>
                  {settingRestricted ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Switch checked={!!folder.restricted} onCheckedChange={handleRestricted} />
                  )}
                  <div className="flex-1">
                    <span className="text-sm flex items-center gap-1.5">
                      <Lock className="h-3.5 w-3.5" />
                      {t('DocControl_restricted_folder', 'Restricted folder')}
                    </span>
                    <p className="text-xs text-muted-foreground">
                      {folder.restricted
                        ? t('DocControl_restricted_folder_desc', 'Only people and roles listed above can access this folder')
                        : t('DocControl_everyone_can_view', 'Everyone with project access can view this folder')}
                    </p>
                  </div>
                </>
              ) : (
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <Info className="h-3.5 w-3.5" />
                  {folder.restricted
                    ? t('DocControl_restricted_folder', 'Restricted folder')
                    : t('DocControl_everyone_can_view', 'Everyone with project access can view this folder')}
                </p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default FolderShareDialog
